import type { UserType } from "@shared/types";
import { Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { StudioButton } from "@/features/studio/theme";
import { cn } from "@/lib/utils";
import { useRoleActions } from "../hooks/use-role-actions";

export interface RoleSwitchPromptProps {
	/** The sentence that says why a switch is needed. */
	message: string;
	/** The role the button switches to. */
	role: UserType;
	/** Shows a dismiss button when given. */
	onDismiss?: () => void;
	className?: string;
}

/**
 * A notice with a button that switches to `role` and stays on the current
 * page.
 */
export function RoleSwitchPrompt({
	message,
	role,
	onDismiss,
	className,
}: RoleSwitchPromptProps): React.JSX.Element {
	const { t } = useTranslation();
	const { pendingRole, switchTo } = useRoleActions();
	const busy = pendingRole !== null;

	return (
		<div
			className={cn(
				"flex flex-wrap items-center gap-3 rounded-xl border border-dashboard-info-border bg-dashboard-info-bg-soft px-4 py-3",
				className,
			)}
		>
			<p className="min-w-0 flex-1 text-sm text-dashboard-info-text">
				{message}
			</p>
			<StudioButton
				variant="primary"
				size="sm"
				type="button"
				disabled={busy}
				onClick={() => void switchTo(role, { stay: true })}
			>
				{busy && (
					<Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
				)}
				{t("roleSwitcher.switchTo", {
					role: t(`roleSession.roleNames.${role}`),
				})}
			</StudioButton>
			{onDismiss && (
				<button
					type="button"
					onClick={onDismiss}
					aria-label={t("roleSwitcher.dismiss")}
					className="flex h-9 w-9 items-center justify-center rounded-full text-dashboard-muted hover:bg-dashboard-divider hover:text-dashboard-text"
				>
					<X className="h-4 w-4" />
				</button>
			)}
		</div>
	);
}
